import { computed, ref, Ref } from 'vue';
import { AttachmentModel } from '../models/attachment.model';
import { MediaModel } from '../models/media.model';
import { mediaUtil } from '../utils/mediaUtil';

const DEFAULT_THUMBNAIL_COUNT = 4;

export interface UseMediaAttachmentsGridOptions {
  thumbnailCount?: number;
}

export const useMediaAttachmentsGrid = (
  attachments: Ref<AttachmentModel[] | null | undefined>,
  profileId: Ref<number | null | undefined>,
  options: UseMediaAttachmentsGridOptions = {}
) => {
  const thumbnailCount = options.thumbnailCount ?? DEFAULT_THUMBNAIL_COUNT;

  // Tracks media that has been removed locally via the carousel; prevents the
  // grid from re-rendering a media that the user just deleted while the
  // parent prop list (still containing it) propagates a new value.
  const removedMediaIds = ref<Set<number>>(new Set());

  const mediaList = computed<MediaModel[]>(() => {
    const source = attachments.value ?? [];
    const profile = profileId.value ?? 0;
    return mediaUtil
      .filterScannedMediaForProfile(source, profile)
      .map(attachment => attachment.media)
      .filter(media => !removedMediaIds.value.has(media.id as number));
  });

  const visibleThumbnailMedia = computed<MediaModel[]>(() => mediaList.value.slice(0, thumbnailCount));

  const hasMoreMedia = computed(() => mediaList.value.length > thumbnailCount);

  const numberOfHiddenMedia = computed(() => (hasMoreMedia.value ? mediaList.value.length - thumbnailCount : 0));

  const gridLayoutClass = computed(() => {
    switch (visibleThumbnailMedia.value.length) {
      case 1:
        return 'grid-layout-1';
      case 2:
        return 'grid-layout-2';
      case 3:
        return 'grid-layout-3';
      default:
        return 'grid-layout-4';
    }
  });

  const getOverlayText = (mediaIndex: number): string => {
    if (mediaIndex < thumbnailCount - 1 || !hasMoreMedia.value) {
      return '';
    }
    return `+${numberOfHiddenMedia.value}`;
  };

  const removeMedia = (mediaId: number): void => {
    removedMediaIds.value = new Set([...removedMediaIds.value, mediaId]);
  };

  return {
    mediaList,
    visibleThumbnailMedia,
    hasMoreMedia,
    numberOfHiddenMedia,
    gridLayoutClass,
    getOverlayText,
    removeMedia,
  };
};
